import type { LemitConsulta, LemitEmpresaData, LemitSocio } from '../../types';
import type { SocioEnriquecido, enriquecerSocio, getConsultasSalvasPorDoc } from './lemit';

// ─────────────────────────────────────────────────────────────────────────────
// Cadeia societária — expansão recursiva do quadro societário.
//
// O quadro societário da Lemit só desce um nível: sócio que é CNPJ vira um beco
// sem saída (o caso CONSORCIO I9 tem 7 sócios e 6 são empresas — nenhum CPF
// aparece no dossiê da raiz). Aqui a expansão é feita em LARGURA, nível a nível:
// cada CNPJ vira uma nova consulta, cujos sócios entram na fila do nível
// seguinte, até só sobrarem CPFs (ou até bater a profundidade / o teto de custo).
//
// Custo: cada CNPJ/CPF inédito é uma consulta PAGA. Por isso o walker (a) usa
// tudo que já está salvo no banco de graça, (b) devolve a prévia de cada nível
// para o chamador confirmar ANTES de gastar e (c) tem teto rígido de consultas
// novas — estourou, para e explica, em vez de continuar cobrando.
// ─────────────────────────────────────────────────────────────────────────────

const soDigitos = (v?: string | null) => String(v || '').replace(/\D/g, '');

/** Documento consultável na Lemit (11 = CPF, 14 = CNPJ). */
export const docConsultavel = (doc?: string | null): boolean => {
    const d = soDigitos(doc);
    return d.length === 11 || d.length === 14;
};

/** Um sócio da cadeia, já situado na árvore (quem o listou e em que nível). */
export interface NoCadeia extends SocioEnriquecido {
    /** 1 = sócio direto da raiz, 2 = sócio de sócio, … */
    nivel: number;
    /** Documento de quem lista este sócio no próprio quadro societário. */
    paiDoc: string;
    paiNome: string;
    /**
     * Documento que já apareceu antes na cadeia (holding repetida ou ciclo).
     * Entra na árvore para o vínculo ficar visível, mas não é reconsultado nem
     * expandido de novo — é o que impede o loop infinito.
     */
    repetido: boolean;
}

/** Prévia de custo de um nível, entregue ao chamador antes de qualquer consulta. */
export interface PreviaNivel {
    nivel: number;
    /** Quantos documentos serão abertos neste nível (cacheados + novos). */
    total: number;
    cacheados: number;
    novosPessoa: number;
    novosEmpresa: number;
    /** null quando o custo por consulta não está configurado em system_settings. */
    custo: number | null;
    /** Consultas novas já pagas nos níveis anteriores desta expansão. */
    novosAcumulados: number;
    custoAcumulado: number | null;
}

export type MotivoParada =
    | 'fim'            // não sobrou CNPJ para expandir: a cadeia acabou
    | 'profundidade'   // bateu maxNivel com CNPJ ainda por abrir
    | 'teto'           // o próximo nível estouraria o teto de consultas novas
    | 'recusado'       // o usuário não confirmou o custo do nível
    | 'cancelado'      // botão "parar"
    | 'limite_conta';  // teto diário da conta Lemit

export interface ResultadoCadeia {
    nos: NoCadeia[];
    niveisPercorridos: number;
    motivoParada: MotivoParada;
    /** Documentos que ficaram sem abrir quando a parada foi teto/profundidade. */
    pendentes: number;
    consultasNovas: number;
    custoGasto: number | null;
    /** Mensagem do erro de limite da conta, quando motivoParada === 'limite_conta'. */
    erro?: string;
}

export interface ExplorarCadeiaOpts {
    raizDoc: string;
    raizNome: string;
    raizData: LemitEmpresaData;
    /** Profundidade máxima (1 = só os sócios diretos, como o botão antigo). */
    maxNivel: number;
    /** Teto RÍGIDO de consultas novas (pagas) em toda a expansão. */
    maxConsultas: number;
    custoPessoa?: number | null;
    custoEmpresa?: number | null;
    /** Sócios que a tela já carregou do banco — evita reconsultar o nível 1. */
    jaEnriquecidos?: Record<string, SocioEnriquecido>;
    /** Confirmação de custo por nível; false interrompe a expansão. */
    confirmarNivel?: (previa: PreviaNivel) => Promise<boolean>;
    onProgresso?: (p: { nivel: number; done: number; total: number; no?: NoCadeia }) => void;
    cancelado?: () => boolean;
    /**
     * As duas idas ao mundo externo (Lemit + banco). Injetáveis só para teste — em
     * produção saem de `./lemit` por import dinâmico, o que mantém este módulo
     * livre do cliente Supabase e portanto executável fora do browser.
     */
    deps?: CadeiaDeps;
}

export interface CadeiaDeps {
    enriquecerSocio: typeof enriquecerSocio;
    getConsultasSalvasPorDoc: typeof getConsultasSalvasPorDoc;
}

interface Alvo {
    socio: LemitSocio;
    doc: string;
    paiDoc: string;
    paiNome: string;
}

/** Nome de exibição de uma empresa/pessoa já consultada. */
const nomeDe = (n: NoCadeia): string => n.nome || (n.data as any)?.razao_social || (n.data as any)?.nome || '';

/** Sócios consultáveis de uma empresa, já amarrados ao pai. */
function alvosDe(data: LemitEmpresaData | null | undefined, paiDoc: string, paiNome: string): Alvo[] {
    return (data?.socios || [])
        .filter((s) => docConsultavel(s?.cpf))
        .map((s) => ({ socio: s, doc: soDigitos(s.cpf), paiDoc, paiNome }));
}

function custoDe(
    novosPessoa: number, novosEmpresa: number,
    custoPessoa?: number | null, custoEmpresa?: number | null,
): number | null {
    const conhecido = (novosPessoa === 0 || custoPessoa != null) && (novosEmpresa === 0 || custoEmpresa != null);
    return conhecido ? novosPessoa * (custoPessoa ?? 0) + novosEmpresa * (custoEmpresa ?? 0) : null;
}

/**
 * Percorre a cadeia societária a partir de uma empresa, nível a nível, até só
 * restarem CPFs. Nunca consulta um documento duas vezes e nunca ultrapassa
 * `maxConsultas` consultas novas.
 */
export async function explorarCadeiaSocietaria(opts: ExplorarCadeiaOpts): Promise<ResultadoCadeia> {
    const deps: CadeiaDeps = opts.deps ?? await import('./lemit');
    const raizDoc = soDigitos(opts.raizDoc);
    const visitados = new Set<string>([raizDoc]);
    const nos: NoCadeia[] = [];

    let fila: Alvo[] = alvosDe(opts.raizData, raizDoc, opts.raizNome);
    let nivel = 0;
    let novosPessoa = 0, novosEmpresa = 0;
    let motivo: MotivoParada = 'fim';
    let pendentes = 0;
    let erro: string | undefined;

    const totalNovos = () => novosPessoa + novosEmpresa;
    const gasto = () => custoDe(novosPessoa, novosEmpresa, opts.custoPessoa, opts.custoEmpresa);

    while (fila.length > 0) {
        if (nivel >= opts.maxNivel) { motivo = 'profundidade'; pendentes = fila.length; break; }
        nivel += 1;

        // Dedup do nível: repetidos entram na árvore (para o vínculo aparecer),
        // mas não são consultados nem expandidos.
        const doNivel: Alvo[] = [];
        const vistosNoNivel = new Set<string>();
        for (const a of fila) {
            if (visitados.has(a.doc) || vistosNoNivel.has(a.doc)) {
                const anterior = nos.find((n) => n.cpf === a.doc);
                nos.push({
                    cpf: a.doc,
                    nome: a.socio.nome || anterior?.nome || '',
                    participacao: a.socio.participacao ?? null,
                    tipo: a.doc.length === 14 ? 'empresa' : 'pessoa',
                    data: null,
                    status: anterior?.status ?? 'ok',
                    source: null,
                    nivel, paiDoc: a.paiDoc, paiNome: a.paiNome, repetido: true,
                    melhorTelefone: anterior?.melhorTelefone,
                    melhorEmail: anterior?.melhorEmail,
                });
                continue;
            }
            vistosNoNivel.add(a.doc);
            doNivel.push(a);
        }

        if (doNivel.length === 0) { motivo = 'fim'; break; }

        // O que já está salvo no banco sai de graça (qualquer consulta anterior,
        // não só as 24h de cache da edge function).
        let salvos: Map<string, LemitConsulta>;
        try {
            salvos = await deps.getConsultasSalvasPorDoc(doNivel.map((a) => a.doc));
        } catch {
            salvos = new Map();
        }

        const jaTem = (doc: string) => !!opts.jaEnriquecidos?.[doc]?.data || salvos.has(doc);
        let nP = 0, nE = 0, cache = 0;
        doNivel.forEach((a) => {
            if (jaTem(a.doc)) { cache++; return; }
            if (a.doc.length === 14) nE++; else nP++;
        });

        // Teto rígido: não estoura nem "só um pouquinho" — para e explica.
        if (totalNovos() + nP + nE > opts.maxConsultas) {
            motivo = 'teto';
            pendentes = doNivel.length;
            nivel -= 1;
            break;
        }

        if (opts.confirmarNivel) {
            const ok = await opts.confirmarNivel({
                nivel,
                total: doNivel.length,
                cacheados: cache,
                novosPessoa: nP,
                novosEmpresa: nE,
                custo: custoDe(nP, nE, opts.custoPessoa, opts.custoEmpresa),
                novosAcumulados: totalNovos(),
                custoAcumulado: gasto(),
            });
            if (!ok) { motivo = 'recusado'; pendentes = doNivel.length; nivel -= 1; break; }
        }

        const proxima: Alvo[] = [];
        let interrompido = false;
        let naoAbertos = 0;

        for (let i = 0; i < doNivel.length; i++) {
            if (opts.cancelado?.()) { motivo = 'cancelado'; interrompido = true; naoAbertos = doNivel.length - i; break; }
            const a = doNivel[i];
            const salvo = salvos.get(a.doc) ?? null;
            const pronto = opts.jaEnriquecidos?.[a.doc];
            let enr: SocioEnriquecido;

            if (pronto?.data) {
                // Já veio do banco na pré-carga da tela: reaproveita, sem custo.
                enr = { ...pronto, nome: pronto.nome || a.socio.nome || '', participacao: a.socio.participacao ?? pronto.participacao ?? null };
            } else {
                try {
                    enr = await deps.enriquecerSocio(a.socio, { salvo });
                } catch (e: any) {
                    // Só erro de LIMITE da conta chega aqui (enriquecerSocio propaga)
                    motivo = 'limite_conta';
                    erro = e?.message || 'Limite da conta Lemit atingido.';
                    interrompido = true;
                    naoAbertos = doNivel.length - i;
                    break;
                }
                if (!salvo) {
                    if (a.doc.length === 14) novosEmpresa++; else novosPessoa++;
                    await new Promise((r) => setTimeout(r, 180));  // a Lemit limita req/s
                }
            }

            visitados.add(a.doc);
            const no: NoCadeia = { ...enr, nivel, paiDoc: a.paiDoc, paiNome: a.paiNome, repetido: false };
            nos.push(no);
            opts.onProgresso?.({ nivel, done: i + 1, total: doNivel.length, no });

            if (no.tipo === 'empresa' && no.data) {
                proxima.push(...alvosDe(no.data as LemitEmpresaData, no.cpf, nomeDe(no)));
            }
        }

        if (interrompido) { pendentes = naoAbertos + proxima.length; break; }
        fila = proxima;
        motivo = 'fim';
    }

    return {
        nos,
        niveisPercorridos: nivel,
        motivoParada: motivo,
        pendentes,
        consultasNovas: totalNovos(),
        custoGasto: gasto(),
        erro,
    };
}

// ── Leitura do resultado ─────────────────────────────────────────────────────

/**
 * Ordena a cadeia em profundidade (cada empresa seguida dos próprios sócios) —
 * é essa a ordem das páginas do PDF e das linhas da árvore na tela.
 */
export function ordenarCadeia(nos: NoCadeia[], raizDoc: string): NoCadeia[] {
    const porPai = new Map<string, NoCadeia[]>();
    nos.forEach((n) => {
        const lista = porPai.get(n.paiDoc);
        if (lista) lista.push(n); else porPai.set(n.paiDoc, [n]);
    });
    const out: NoCadeia[] = [];
    const visitar = (doc: string, guarda: Set<string>) => {
        (porPai.get(doc) || []).forEach((n) => {
            out.push(n);
            if (!n.repetido && !guarda.has(n.cpf)) {
                guarda.add(n.cpf);
                visitar(n.cpf, guarda);
            }
        });
    };
    visitar(soDigitos(raizDoc), new Set([soDigitos(raizDoc)]));
    // Órfão (pai fora da árvore) não pode sumir do relatório
    if (out.length < nos.length) {
        const dentro = new Set(out);
        nos.forEach((n) => { if (!dentro.has(n)) out.push(n); });
    }
    return out;
}

/** Uma pessoa física encontrada na cadeia, com as empresas por onde ela entra. */
export interface PessoaDaCadeia {
    cpf: string;
    nome: string;
    melhorTelefone?: string;
    melhorEmail?: string;
    status: SocioEnriquecido['status'];
    /** Nível mais raso em que aparece. */
    nivel: number;
    /** Empresas que a listam no quadro societário. */
    vias: string[];
}

/**
 * Consolida os CPFs da cadeia — o objetivo final da expansão: "todos os CPFs
 * ligados a todos os CNPJs da empresa", cada um com as empresas por onde chega.
 * Inclui quem não pôde ser enriquecido (o CPF já é o achado).
 */
export function pessoasDaCadeia(nos: NoCadeia[]): PessoaDaCadeia[] {
    const mapa = new Map<string, PessoaDaCadeia>();
    nos.filter((n) => n.tipo === 'pessoa' && n.cpf).forEach((n) => {
        const atual = mapa.get(n.cpf);
        if (!atual) {
            mapa.set(n.cpf, {
                cpf: n.cpf,
                nome: nomeDe(n),
                melhorTelefone: n.melhorTelefone,
                melhorEmail: n.melhorEmail,
                status: n.status,
                nivel: n.nivel,
                vias: n.paiNome ? [n.paiNome] : [],
            });
            return;
        }
        atual.nome = atual.nome || nomeDe(n);
        atual.melhorTelefone = atual.melhorTelefone || n.melhorTelefone;
        atual.melhorEmail = atual.melhorEmail || n.melhorEmail;
        if (n.status === 'ok') atual.status = 'ok';
        atual.nivel = Math.min(atual.nivel, n.nivel);
        if (n.paiNome && !atual.vias.includes(n.paiNome)) atual.vias.push(n.paiNome);
    });
    return Array.from(mapa.values()).sort((a, b) => a.nivel - b.nivel || a.nome.localeCompare(b.nome));
}

/** Contagens para o resumo da tela e do PDF. */
export function resumoCadeia(nos: NoCadeia[]): {
    empresas: number; pessoas: number; comDossie: number; semDossie: number; repetidos: number; nivelMax: number;
} {
    const unicos = new Map<string, NoCadeia>();
    let repetidos = 0;
    nos.forEach((n) => {
        if (n.repetido) { repetidos++; return; }
        if (!unicos.has(n.cpf)) unicos.set(n.cpf, n);
    });
    const lista = Array.from(unicos.values());
    return {
        empresas: lista.filter((n) => n.tipo === 'empresa').length,
        pessoas: lista.filter((n) => n.tipo === 'pessoa').length,
        comDossie: lista.filter((n) => n.data).length,
        semDossie: lista.filter((n) => !n.data).length,
        repetidos,
        nivelMax: nos.reduce((m, n) => Math.max(m, n.nivel), 0),
    };
}

/** Rótulo humano do motivo da parada (tela e PDF dizem a mesma coisa). */
export function textoMotivoParada(r: ResultadoCadeia): string {
    switch (r.motivoParada) {
        case 'fim': return 'Cadeia percorrida até o fim — não restou CNPJ por abrir.';
        case 'profundidade': return `Parou na profundidade máxima (${r.niveisPercorridos} ${r.niveisPercorridos === 1 ? 'nível' : 'níveis'}) — ${r.pendentes} documento(s) ficaram sem abrir.`;
        case 'teto': return `Parou no teto de consultas novas — o próximo nível tinha ${r.pendentes} documento(s) por abrir.`;
        case 'recusado': return `Expansão interrompida antes de pagar o próximo nível (${r.pendentes} documento(s) por abrir).`;
        case 'cancelado': return 'Expansão interrompida pelo usuário.';
        case 'limite_conta': return `Limite da conta Lemit atingido — ${r.erro || 'tente novamente mais tarde'}.`;
        default: return '';
    }
}
